import React, { useState, useRef, useCallback, useEffect } from "react";
import ReactCrop, { type Crop } from "react-image-crop";
import { useTranslation } from "react-i18next";
import "react-image-crop/dist/ReactCrop.css";

import { ImageEditSettings } from "types/Pictures";
import CancelIcon from "assets/icons/CancelIcon";
import CheckedIcon from "assets/icons/CheckedIcon";
import RefreshIcon from "assets/icons/RefreshIcon";

interface ImageEditorProps {
  imageUrl: string;
  onSave: (file: File, settings: ImageEditSettings) => void;
  onCancel: () => void;
  isUploading?: boolean;
  pictureId?: number;
}

const ImageEditor: React.FC<ImageEditorProps> = ({
  imageUrl,
  onSave,
  onCancel,
  isUploading = false,
  pictureId,
}) => {
  const { t } = useTranslation();
  const imgRef = useRef<HTMLImageElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  // Backend dimension limits
  const MAX_IMAGE_PIXELS = Math.floor((1024 * 1024 * 1024) / 4 / 3); // ≈ 89M pixels
  const MAX_TOTAL_PIXELS = 2 * MAX_IMAGE_PIXELS; // ≈ 178M pixels

  const [settings, setSettings] = useState<ImageEditSettings>({
    rotation: 0,
    brightness: 0,
    crop: null,
  });

  const [crop, setCrop] = useState<Crop>();
  const [completedCrop, setCompletedCrop] = useState<Crop>();
  const [isProcessing, setIsProcessing] = useState(false);
  const [localImageUrl, setLocalImageUrl] = useState<string | null>(null);
  const [isLoadingImage, setIsLoadingImage] = useState(true);
  const [corsError, setCorsError] = useState(false);
  const [dimensionWarning, setDimensionWarning] = useState<string | null>(null);

  // Clean image fetching without axiosInstance to avoid auth headers + preflight
  const fetchImageBlob = useCallback(async (url: string): Promise<Blob> => {
    console.log(`Fetching image: ${url}`);

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000);

    try {
      const response = await fetch(url, {
        mode: "cors",
        credentials: "omit",
        signal: controller.signal,
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      return await response.blob();
    } catch (fetchError) {
      console.warn("Image fetch failed:", fetchError);
      throw fetchError;
    } finally {
      clearTimeout(timeoutId);
    }
  }, []);

  useEffect(() => {
    const loadImage = async () => {
      try {
        setIsLoadingImage(true);
        setCorsError(false);

        const blob = await fetchImageBlob(imageUrl);
        const url = URL.createObjectURL(blob);
        setLocalImageUrl(url);
      } catch (error) {
        console.error("Failed to fetch image:", error);
        setCorsError(true);

        // Use original URL as fallback - this will work for same-origin images
        // or images with proper CORS headers
        setLocalImageUrl(imageUrl);
      } finally {
        setIsLoadingImage(false);
      }
    };

    loadImage();
  }, [imageUrl, fetchImageBlob]);

  // Cleanup blob URL when component unmounts or imageUrl changes
  useEffect(() => {
    return () => {
      if (localImageUrl && localImageUrl.startsWith("blob:")) {
        URL.revokeObjectURL(localImageUrl);
      }
    };
  }, [localImageUrl]);

  // Check dimensions when image loads or settings change
  const checkDimensions = useCallback(
    (image: HTMLImageElement) => {
      if (!image) return;

      const scaleX = image.naturalWidth / image.width;
      const scaleY = image.naturalHeight / image.height;

      // Calculate final dimensions based on current settings
      let sourceWidth = completedCrop
        ? completedCrop.width * scaleX
        : image.naturalWidth;
      let sourceHeight = completedCrop
        ? completedCrop.height * scaleY
        : image.naturalHeight;

      // Account for rotation dimension swap
      const shouldSwapDimensions = Math.abs(settings.rotation % 180) === 90;
      let finalWidth = sourceWidth;
      let finalHeight = sourceHeight;

      if (shouldSwapDimensions) {
        [finalWidth, finalHeight] = [finalHeight, finalWidth];
      }

      const totalPixels = finalWidth * finalHeight;

      if (totalPixels > MAX_TOTAL_PIXELS) {
        const maxDimension = Math.sqrt(MAX_TOTAL_PIXELS);
        setDimensionWarning(
          `Warning: Current dimensions (${Math.round(finalWidth)}×${Math.round(
            finalHeight,
          )}) exceed backend limits. Image will be automatically resized to fit within ${Math.round(
            maxDimension,
          )}×${Math.round(maxDimension)} pixels.`,
        );
      } else {
        setDimensionWarning(null);
      }
    },
    [completedCrop, settings.rotation, MAX_TOTAL_PIXELS],
  );

  const onImageLoad = useCallback(
    (e: React.SyntheticEvent<HTMLImageElement>) => {
      const image = e.target as HTMLImageElement;
      checkDimensions(image);
    },
    [checkDimensions],
  );

  // Update dimension check when settings change
  useEffect(() => {
    if (imgRef.current) {
      checkDimensions(imgRef.current);
    }
  }, [settings, completedCrop, checkDimensions]);

  const rotateImage = (degrees: number) => {
    setSettings((prev) => ({
      ...prev,
      rotation: (prev.rotation + degrees) % 360,
    }));
  };

  const updateBrightness = (value: number) => {
    setSettings((prev) => ({
      ...prev,
      brightness: value,
    }));
  };

  const clearCrop = () => {
    setCrop(undefined);
    setCompletedCrop(undefined);
  };

  const resetSettings = () => {
    setSettings({
      rotation: 0,
      brightness: 0,
      crop: null,
    });
    setCrop(undefined);
    setCompletedCrop(undefined);
    setDimensionWarning(null);
  };

  // Fixed canvas rendering function with proper dimension limits
  const renderToCanvas = useCallback(
    (
      image: HTMLImageElement,
      canvas: HTMLCanvasElement,
      crop: Crop | null,
      rotation: number,
      brightness: number,
    ) => {
      const ctx = canvas.getContext("2d", { willReadFrequently: true });
      if (!ctx) return;

      // Clear any previous content
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      const scaleX = image.naturalWidth / image.width;
      const scaleY = image.naturalHeight / image.height;

      // Calculate source dimensions (what we're taking from the original image)
      const sourceX = crop ? crop.x * scaleX : 0;
      const sourceY = crop ? crop.y * scaleY : 0;
      const sourceWidth = crop ? crop.width * scaleX : image.naturalWidth;
      const sourceHeight = crop ? crop.height * scaleY : image.naturalHeight;

      // Determine if we need to swap dimensions for 90/270 degree rotations
      const shouldSwapDimensions = Math.abs(rotation % 180) === 90;

      // Calculate base output dimensions (accounting for rotation)
      let baseWidth = sourceWidth;
      let baseHeight = sourceHeight;

      if (shouldSwapDimensions) {
        [baseWidth, baseHeight] = [sourceHeight, sourceWidth];
      }

      // FIXED: Apply dimension limits to prevent backend errors
      const totalPixels = baseWidth * baseHeight;
      let finalWidth = baseWidth;
      let finalHeight = baseHeight;

      if (totalPixels > MAX_TOTAL_PIXELS) {
        // Scale down to fit within limits while maintaining aspect ratio
        const scale = Math.sqrt(MAX_TOTAL_PIXELS / totalPixels);
        finalWidth = Math.floor(baseWidth * scale);
        finalHeight = Math.floor(baseHeight * scale);

        console.log(
          `Scaling image from ${Math.round(baseWidth)}×${Math.round(
            baseHeight,
          )} to ${finalWidth}×${finalHeight} to fit backend limits`,
        );
      }

      // FIXED: Set canvas dimensions without pixelRatio multiplication
      canvas.width = Math.max(1, finalWidth);
      canvas.height = Math.max(1, finalHeight);

      // Set canvas display size for proper rendering quality
      canvas.style.width = `${finalWidth}px`;
      canvas.style.height = `${finalHeight}px`;

      // FIXED: Use scale factor for drawing context instead of pixelRatio
      const drawScale = Math.min(
        finalWidth / baseWidth,
        finalHeight / baseHeight,
      );

      // Set proper rendering quality
      ctx.imageSmoothingQuality = "high";
      ctx.imageSmoothingEnabled = true;

      // Calculate centers for rotation
      const centerX = finalWidth / 2;
      const centerY = finalHeight / 2;

      // Save context state
      ctx.save();

      // Apply transformations
      ctx.translate(centerX, centerY);
      ctx.rotate((rotation * Math.PI) / 180);

      // Calculate the size to draw the source image
      const drawWidth = sourceWidth * drawScale;
      const drawHeight = sourceHeight * drawScale;

      ctx.translate(-drawWidth / 2, -drawHeight / 2);

      // Apply brightness filter
      ctx.filter = `brightness(${100 + brightness}%)`;

      try {
        ctx.drawImage(
          image,
          sourceX,
          sourceY,
          sourceWidth,
          sourceHeight,
          0,
          0,
          drawWidth,
          drawHeight,
        );
      } catch (error) {
        console.error("Error drawing image to canvas:", error);
      }

      ctx.restore();

      // Ensure canvas has content
      try {
        // Test if canvas has content by reading a pixel
        ctx.getImageData(0, 0, 1, 1);
      } catch (e) {
        console.error("Canvas appears to be empty or tainted:", e);
      }
    },
    [MAX_TOTAL_PIXELS],
  );

  const handleSave = useCallback(async () => {
    if (!imgRef.current || !canvasRef.current) {
      return;
    }

    setIsProcessing(true);

    try {
      // Use the renderToCanvas function
      renderToCanvas(
        imgRef.current,
        canvasRef.current,
        completedCrop || null,
        settings.rotation,
        settings.brightness,
      );

      // Define handler functions
      const createFileAndSave = (blob: Blob) => {
        const file = new File([blob], "edited-image.jpg", {
          type: "image/jpeg",
          lastModified: Date.now(),
        });

        const finalSettings: ImageEditSettings = {
          ...settings,
          crop: completedCrop
            ? {
                x: completedCrop.x,
                y: completedCrop.y,
                width: completedCrop.width,
                height: completedCrop.height,
              }
            : null,
        };

        // Call the onSave callback - parent will handle the API flow
        onSave(file, finalSettings);
        setIsProcessing(false);
      };

      const handleBlobError = () => {
        alert(
          corsError
            ? "Failed to process the image due to CORS restrictions. Please ensure your backend serves images with proper CORS headers or implement a server-side image proxy."
            : "Failed to process the image. Please try again.",
        );
        setIsProcessing(false);
      };

      // Try to create a blob using fallback approaches if needed
      try {
        // First try with toBlob
        canvasRef.current.toBlob(
          (blob) => {
            if (!blob) {
              console.error(
                "Failed to create blob from canvas, attempting fallback",
              );
              // Fallback approach - use toDataURL and convert to blob
              try {
                const dataURL = canvasRef.current!.toDataURL(
                  "image/jpeg",
                  0.95,
                );
                fetch(dataURL)
                  .then((res) => res.blob())
                  .then((blobFromDataURL) => {
                    if (!blobFromDataURL || blobFromDataURL.size === 0) {
                      throw new Error("Empty blob from data URL conversion");
                    }
                    createFileAndSave(blobFromDataURL);
                  })
                  .catch((error) => {
                    console.error("Fallback blob creation failed:", error);
                    handleBlobError();
                  });
              } catch (dataUrlError) {
                console.error("toDataURL fallback failed:", dataUrlError);
                handleBlobError();
              }
              return;
            }

            // If we have a valid blob, proceed
            createFileAndSave(blob);
          },
          "image/jpeg",
          0.95,
        );
      } catch (blobError) {
        console.error("toBlob failed with error:", blobError);
        handleBlobError();
      }
    } catch (error) {
      console.error("Error applying edits:", error);

      if (error instanceof Error && error.message.includes("tainted")) {
        alert(
          "Cannot edit image due to CORS restrictions. Please ensure your backend serves images with proper CORS headers or implement a server-side image proxy.",
        );
      } else {
        alert(
          "An error occurred while processing the image. Please try again.",
        );
      }

      setIsProcessing(false);
    }
  }, [completedCrop, settings, renderToCanvas, onSave, corsError]);

  const handleImageError = useCallback(
    (e: React.SyntheticEvent<HTMLImageElement>) => {
      console.error("Image failed to load");
      const img = e.target as HTMLImageElement;

      // Try removing crossOrigin if it was set
      if (img.crossOrigin) {
        console.log("Retrying without crossOrigin...");
        const currentSrc = img.src; // Store current src
        img.crossOrigin = null;
        // Force reload by setting src to empty string first, then back to original
        img.src = "";
        setTimeout(() => {
          img.src = currentSrc; // Set back to original src
        }, 10);
      }
    },
    [],
  );

  return (
    <div className="fixed z-[110] top-0 left-0 w-full h-full bg-black/80 flex justify-center items-center">
      <div className="w-[95%] max-w-6xl bg-white rounded-lg overflow-hidden">
        {/* Header */}
        <div className="bg-gray-100 p-4 flex justify-between items-center">
          <h2 className="text-xl font-semibold">
            {t("_published_picture._edit_image")}
          </h2>
          <button
            onClick={onCancel}
            className="p-2 hover:bg-gray-200 rounded-full"
          >
            <CancelIcon width={24} height={24} color="black" />
          </button>
        </div>

        {/* Dimension Warning */}
        {dimensionWarning && (
          <div className="bg-orange-50 border-l-4 border-orange-400 p-4 mx-4 mt-4 rounded">
            <div className="flex">
              <div className="flex-shrink-0">
                <div className="text-orange-400">⚠️</div>
              </div>
              <div className="ml-3">
                <p className="text-sm text-orange-700">
                  <strong>Dimension Limit Warning</strong>
                </p>
                <p className="text-xs text-orange-600 mt-1">
                  {dimensionWarning}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Main Content */}
        <div className="flex flex-col lg:flex-row h-[80vh]">
          {/* Image Area */}
          <div className="flex-1 p-4 bg-gray-50 flex flex-col justify-center items-center">
            {isLoadingImage ? (
              <div className="flex flex-col items-center justify-center space-y-2">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
                <span className="text-sm text-gray-600">Loading image...</span>
              </div>
            ) : localImageUrl ? (
              <div className="w-full h-full flex justify-center items-center">
                {corsError && (
                  <div className="absolute top-2 right-2 bg-yellow-100 border border-yellow-400 text-yellow-800 px-3 py-1 rounded text-sm">
                    ⚠️ CORS issue detected - editing may be limited
                  </div>
                )}

                {/* FIXED: Separate rotation and zoom transforms */}
                <div
                  className="relative transition-transform duration-200 ease-out"
                  style={{
                    transform: `rotate(${settings.rotation}deg)`,
                    transformOrigin: "center",
                    filter: `brightness(${100 + settings.brightness}%)`,
                  }}
                >
                  <div className="relative">
                    <ReactCrop
                      crop={crop}
                      onChange={(c) => setCrop(c)}
                      onComplete={(c) => setCompletedCrop(c)}
                      aspect={undefined}
                      minWidth={50}
                      minHeight={50}
                    >
                      <img
                        ref={imgRef}
                        alt="Edit target"
                        src={localImageUrl}
                        style={{
                          maxHeight: "60vh",
                          maxWidth: "100%",
                          display: "block",
                        }}
                        onLoad={onImageLoad}
                        onError={handleImageError}
                        crossOrigin={corsError ? undefined : "anonymous"}
                      />
                    </ReactCrop>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-red-500 text-center">
                <p>Failed to load image</p>
                <p className="text-sm mt-2">
                  Please ensure the image URL is accessible and CORS is properly
                  configured.
                </p>
              </div>
            )}
          </div>

          {/* Tools Panel */}
          <div className="w-full lg:w-80 border-l bg-white overflow-y-auto">
            <div className="p-4 space-y-6">
              {/* CORS Warning */}
              {corsError && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                  <div className="flex">
                    <div className="flex-shrink-0">⚠️</div>
                    <div className="ml-2">
                      <h3 className="text-sm font-medium text-yellow-800">
                        CORS Restriction Detected
                      </h3>
                      <p className="text-xs text-yellow-700 mt-1">
                        Image editing may be limited. Consider implementing a
                        server-side image proxy for better functionality.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Rotation Controls */}
              <div>
                <h3 className="text-lg font-medium mb-3">
                  {t("_published_picture._rotation")}
                </h3>
                <div className="flex space-x-2">
                  <button
                    onClick={() => rotateImage(-90)}
                    className="flex-1 py-2 px-4 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
                  >
                    ↺ 90°
                  </button>
                  <button
                    onClick={() => rotateImage(90)}
                    className="flex-1 py-2 px-4 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
                  >
                    ↻ 90°
                  </button>
                </div>
                <p className="text-sm text-gray-600 mt-1">
                  {t("_published_picture._current_rotation")}:{" "}
                  {settings.rotation}°
                </p>
              </div>

              {/* Brightness Control */}
              <div>
                <h3 className="text-lg font-medium mb-3">
                  {t("_published_picture._brightness")}
                </h3>
                <input
                  type="range"
                  min="-100"
                  max="100"
                  value={settings.brightness}
                  onChange={(e) => updateBrightness(Number(e.target.value))}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                />
                <div className="flex justify-between text-sm text-gray-600 mt-1">
                  <span>-100</span>
                  <span>{settings.brightness}</span>
                  <span>+100</span>
                </div>
              </div>

              {/* Crop Controls */}
              <div>
                <h3 className="text-lg font-medium mb-1">
                  {t("_published_picture._crop")}
                </h3>
                <div className="space-y-2">
                  <p className="text-sm text-gray-600">
                    {completedCrop
                      ? `${Math.round(completedCrop.width)} × ${Math.round(
                          completedCrop.height,
                        )}`
                      : t("_published_picture._crop_area_instruction")}
                  </p>
                  {!crop && !completedCrop && (
                    <button
                      onClick={() => {
                        if (imgRef.current) {
                          const newCrop = {
                            unit: "%" as const,
                            x: 10,
                            y: 10,
                            width: 80,
                            height: 80,
                          };
                          setCrop(newCrop);
                        }
                      }}
                      className="w-full py-1 px-2 text-sm bg-blue-100 text-blue-700 rounded hover:bg-blue-200 transition-colors"
                    >
                      {t("_published_picture._enable_crop")}
                    </button>
                  )}
                  {(crop || completedCrop) && (
                    <button
                      onClick={clearCrop}
                      className="w-full py-1 px-2 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors"
                    >
                      {t("_published_picture._clear_crop")}
                    </button>
                  )}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="space-y-3 pt-4 border-t">
                <button
                  onClick={resetSettings}
                  disabled={
                    settings.rotation === 0 &&
                    settings.brightness === 0 &&
                    !crop &&
                    !completedCrop
                  }
                  className={`w-full flex items-center justify-center py-2 px-4 ${
                    settings.rotation !== 0 ||
                    settings.brightness !== 0 ||
                    crop ||
                    completedCrop
                      ? "bg-gray-500 text-white hover:bg-gray-600"
                      : "bg-gray-300 text-gray-500 cursor-not-allowed"
                  } rounded transition-colors`}
                >
                  <RefreshIcon
                    width={20}
                    height={20}
                    color={
                      settings.rotation !== 0 ||
                      settings.brightness !== 0 ||
                      crop ||
                      completedCrop
                        ? "white"
                        : "#999"
                    }
                  />
                  <span className="ml-2">{t("_published_picture._reset")}</span>
                </button>

                <button
                  onClick={handleSave}
                  disabled={isProcessing || isUploading}
                  className="w-full flex items-center justify-center py-2 px-4 bg-green-500 text-white rounded hover:bg-green-600 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                >
                  <CheckedIcon width={20} height={20} color="white" />
                  <span className="ml-2">
                    {isProcessing || isUploading
                      ? t("_published_picture._processing")
                      : t("_published_picture._save")}
                  </span>
                </button>

                <button
                  onClick={onCancel}
                  className="w-full py-2 px-4 border border-gray-300 text-gray-700 rounded hover:bg-gray-50 transition-colors"
                >
                  {t("_published_picture._cancel")}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Hidden canvas for processing */}
        <canvas ref={canvasRef} className="hidden" />
      </div>
    </div>
  );
};

export default ImageEditor;
